// [claude-code 2026-03-09] Added cancel on click during speaking/thinking, mic denied-state UI
// [claude-code 2026-03-12] Switched from independent useVoiceAssistant() to shared VoiceContext
// [Codex 2026-07-03] S118: removed parallel retired session startup; VoiceContext
// owns CAO voice control through the provider-native text route and local audio bridges.
import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { useVoice } from "../../contexts/VoiceContext";
import { resolveVoiceOrbState } from "../../types/voice";
import { VoiceAuroraOrb } from "./VoiceAuroraOrb";

const ER_THRESHOLD = -1.5;
const INTERVENTION_COOLDOWN_MS = 10 * 60_000;

interface HeaderVoiceControlProps {
  compact?: boolean;
}

interface PsychAssistScoreEvent {
  score?: number;
  timestamp?: number;
}

function readCurrentErScore(): number {
  try {
    const raw = localStorage.getItem("psychassist_current_score");
    const score = raw ? parseFloat(raw) : 0;
    return Number.isFinite(score) ? score : 0;
  } catch {
    return 0;
  }
}

export function HeaderVoiceControl({
  compact = false,
}: HeaderVoiceControlProps) {
  const {
    enabled,
    runtimeState,
    voiceWidgetHidden,
    isSupported,
    micPermission,
    setVoiceWidgetHidden,
    toggleEnabled,
    respondToInfraction,
    cancel,
  } = useVoice();

  const [currentScore, setCurrentScore] = useState(0);
  const cooldownUntilRef = useRef<number>(0);

  const evaluateIntervention = useCallback(
    (now: number) => {
      if (!enabled) return;
      if (now < cooldownUntilRef.current) return;
      const hitScoreThreshold = currentScore <= ER_THRESHOLD;

      if (!hitScoreThreshold) return;

      cooldownUntilRef.current = now + INTERVENTION_COOLDOWN_MS;
      void respondToInfraction({
        erScore: currentScore,
        infractionCount: 0,
      });
    },
    [currentScore, enabled, respondToInfraction],
  );

  useEffect(() => {
    setCurrentScore(readCurrentErScore());

    const scoreInterval = window.setInterval(() => {
      setCurrentScore(readCurrentErScore());
    }, 1500);

    const handleScore = (event: Event) => {
      const detail = (event as CustomEvent<PsychAssistScoreEvent>).detail;
      if (typeof detail?.score === "number" && Number.isFinite(detail.score)) {
        setCurrentScore(detail.score);
      }
    };

    window.addEventListener("psychassist:score", handleScore as EventListener);

    return () => {
      clearInterval(scoreInterval);
      window.removeEventListener(
        "psychassist:score",
        handleScore as EventListener,
      );
    };
  }, [evaluateIntervention]);

  useEffect(() => {
    const now = Date.now();
    evaluateIntervention(now);
  }, [currentScore, evaluateIntervention]);

  const orbState = resolveVoiceOrbState(runtimeState, false);
  const showHeaderOrb = voiceWidgetHidden;
  const [renderHeaderOrb, setRenderHeaderOrb] = useState(showHeaderOrb);

  useEffect(() => {
    if (showHeaderOrb) {
      setRenderHeaderOrb(true);
      return;
    }
    const timer = window.setTimeout(() => setRenderHeaderOrb(false), 180);
    return () => window.clearTimeout(timer);
  }, [showHeaderOrb]);

  const isBusy = runtimeState === "thinking" || runtimeState === "speaking";
  const isMicDenied = micPermission === "denied";
  const isDisabled = !isSupported;

  const handleClick = useCallback(() => {
    if (voiceWidgetHidden) {
      if (enabled) {
        if (isBusy) cancel();
        toggleEnabled();
        return;
      }
      window.dispatchEvent(new CustomEvent("fintheon:voice-widget-close"));
      setVoiceWidgetHidden(false);
      return;
    }
    if (enabled) {
      setVoiceWidgetHidden(true);
      return;
    }
    toggleEnabled();
  }, [
    isBusy,
    enabled,
    voiceWidgetHidden,
    cancel,
    setVoiceWidgetHidden,
    toggleEnabled,
  ]);

  const getTitle = () => {
    if (!isSupported) return "Voice recognition unavailable in this browser";
    if (voiceWidgetHidden) {
      return enabled
        ? "Disable hidden voice assistant"
        : "Close hidden voice assistant";
    }
    if (enabled) return "Return voice dock to header";
    if (isBusy) return "Cancel current voice operation";
    if (runtimeState === "error") {
      return "Voice input unavailable — check your microphone";
    }
    if (isMicDenied) return "Open voice assistant";
    return "Enable voice assistant";
  };

  return (
    <>
      <style>{`
        .mic-dormant-shimmer {
          animation: mic-brighter-shimmer 2s ease-in-out infinite;
        }
        .voice-header-control-stack {
          position: relative;
          display: grid;
          place-items: center;
          width: ${compact ? "24px" : "28px"};
          height: ${compact ? "24px" : "28px"};
        }
        .voice-header-control-layer {
          grid-area: 1 / 1;
          display: grid;
          place-items: center;
          transition:
            opacity 180ms ease,
            transform 180ms ease;
        }
        .voice-header-control-layer[data-visible="false"] {
          opacity: 0;
          transform: scale(0.82);
          pointer-events: none;
        }
        .voice-header-control-layer[data-visible="true"] {
          opacity: 1;
          transform: scale(1);
        }
        @keyframes mic-brighter-shimmer {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        className={`relative toolbar-icon-btn transition-colors ${
          compact ? "!h-6 !w-6" : ""
        } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        title={getTitle()}
        data-voice-widget-hidden={voiceWidgetHidden ? "true" : "false"}
      >
        <span className="voice-header-control-stack">
          <span
            className="voice-header-control-layer"
            data-voice-header-layer="mic"
            data-visible={showHeaderOrb ? "false" : "true"}
          >
            {enabled ? (
              <Mic className="w-3 h-3 toolbar-icon-active mic-dormant-shimmer" />
            ) : (
              <MicOff className="w-3 h-3 text-zinc-500" />
            )}
          </span>
          {renderHeaderOrb ? (
            <span
              className="voice-header-control-layer"
              data-voice-header-layer="orb"
              data-visible={showHeaderOrb ? "true" : "false"}
            >
              <VoiceAuroraOrb state={orbState} compact={compact} />
            </span>
          ) : null}
        </span>
      </button>
    </>
  );
}
